import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Search, ArrowRight, CheckCircle, AlertCircle, Loader2,
  RotateCcw, DollarSign, ArrowLeftRight, Camera, X, ChevronRight,
  User, Mail, Phone, MapPin, Calendar, FileText, Upload, Truck,
  Wallet, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Return reasons
const returnReasons = [
  { value: 'damaged', label: 'Product Damaged' },
  { value: 'defective', label: 'Product Defective' },
  { value: 'wrong_item', label: 'Wrong Item Received' },
  { value: 'not_as_described', label: 'Not as Described' },
  { value: 'size_issue', label: 'Size/Fit Issue' },
  { value: 'changed_mind', label: 'Changed Mind' },
  { value: 'quality_issue', label: 'Quality Not Satisfactory' },
  { value: 'other', label: 'Other' }
];

// Return type labels
const returnTypeLabels = {
  return_refund: { label: 'Return & Refund', description: 'Return items and get refund', icon: RotateCcw },
  refund_only: { label: 'Refund Only', description: 'Keep items, get partial refund', icon: DollarSign },
  replace_same: { label: 'Replace (Same)', description: 'Exchange for same product', icon: ArrowLeftRight },
  replace_different: { label: 'Replace (Different)', description: 'Exchange for different product', icon: ArrowLeftRight }
};

const CustomerReturnPortal = () => {
  // Steps: lookup, select_items, select_type, confirm, success
  const [step, setStep] = useState('lookup');
  const [loading, setLoading] = useState(false);
  
  // Lookup form
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  
  // Order data
  const [orderData, setOrderData] = useState(null);
  const [returnTypes, setReturnTypes] = useState({});
  const [returnWindowDays, setReturnWindowDays] = useState(14);
  // Per-reason policies from the tenant's return rule (server-driven).
  const [serverReasons, setServerReasons] = useState([]);
  const [selfShipAllowed, setSelfShipAllowed] = useState(true);
  const [uploadingPhotoSku, setUploadingPhotoSku] = useState(null);
  
  // Selected items
  const [selectedItems, setSelectedItems] = useState({});
  
  // Return details
  const [returnType, setReturnType] = useState('return_refund');
  const [customerNotes, setCustomerNotes] = useState('');
  const [pickupPreference, setPickupPreference] = useState('courier');
  // Phase 2 — refund method config from the rule
  const [refundMethodOptions, setRefundMethodOptions] = useState(['original']);
  const [refundMethod, setRefundMethod] = useState('original');
  const [storeCreditBonusPercent, setStoreCreditBonusPercent] = useState(0);
  // Phase 3 — tenant portal branding (applied after order lookup)
  const [branding, setBranding] = useState(null);
  
  // Success data
  const [returnResult, setReturnResult] = useState(null);
  // Lookup result — { eligible, reason } after order is found
  const [lookupResult, setLookupResult] = useState(null);
  
  // Look up order
  const handleLookup = async () => {
    if (!orderNumber.trim() || !email.trim()) {
      toast.error('Please enter order number and email');
      return;
    }
    
    setLoading(true);
    setLookupResult(null);
    setOrderData(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/v1/returns/portal/lookup?order_identifier=${encodeURIComponent(orderNumber)}&email=${encodeURIComponent(email)}`
      );
      
      // Always show the fetched order + branding, eligible or not
      setOrderData(response.data.order);
      if (response.data.branding) setBranding(response.data.branding);
      setLookupResult({
        eligible: !!response.data.eligible,
        reason: response.data.reason || '',
      });
      
      if (response.data.eligible) {
        setReturnTypes(response.data.return_types || {});
        setReturnWindowDays(response.data.return_window_days || 14);
        setServerReasons(Array.isArray(response.data.return_reasons) ? response.data.return_reasons : []);
        setSelfShipAllowed(response.data.self_ship_allowed !== false);
        // Phase 2 — refund method config
        const opts = Array.isArray(response.data.refund_method_options) && response.data.refund_method_options.length
          ? response.data.refund_method_options
          : ['original'];
        setRefundMethodOptions(opts);
        const def = response.data.default_refund_method && opts.includes(response.data.default_refund_method)
          ? response.data.default_refund_method
          : opts[0];
        setRefundMethod(def);
        setStoreCreditBonusPercent(Number(response.data.store_credit_bonus_percent || 0));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Order not found. Please check your order number and email.');
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle item selection
  const toggleItem = (sku) => {
    setSelectedItems(prev => {
      const newItems = { ...prev };
      if (newItems[sku]) {
        delete newItems[sku];
      } else {
        const orderItem = orderData.items.find(i => i.sku === sku);
        newItems[sku] = {
          sku,
          product_name: orderItem?.name || orderItem?.title,
          quantity_ordered: orderItem?.quantity || 1,
          quantity_returning: orderItem?.quantity || 1,
          reason: '',
          reason_notes: '',
          photos: []
        };
      }
      return newItems;
    });
  };
  
  // Update item quantity
  const updateItemQuantity = (sku, qty) => {
    setSelectedItems(prev => ({
      ...prev,
      [sku]: { ...prev[sku], quantity_returning: Math.min(qty, prev[sku].quantity_ordered) }
    }));
  };
  
  // Update item reason
  const updateItemReason = (sku, reason) => {
    setSelectedItems(prev => ({
      ...prev,
      [sku]: { ...prev[sku], reason }
    }));
  };

  // Per-reason policy helpers (server-driven reasons)
  const findReasonPolicy = (reasonValue) => {
    if (!reasonValue) return null;
    return serverReasons.find(
      r => r.id === reasonValue || (r.name || '').toLowerCase() === String(reasonValue).toLowerCase()
    ) || null;
  };

  const CORE_TYPES = ['return_refund', 'refund_only', 'replace_same', 'replace_different'];

  // Intersection of allowed return types across the selected items' reasons.
  const getAllowedReturnTypes = () => {
    const ruleLevel = CORE_TYPES.filter(t => returnTypes[t]);
    if (serverReasons.length === 0) return ruleLevel;
    let allowed = new Set(CORE_TYPES);
    let anyPolicy = false;
    Object.values(selectedItems).forEach(item => {
      const policy = findReasonPolicy(item.reason);
      const types = (policy?.allowed_return_types || []).filter(t => CORE_TYPES.includes(t));
      if (types.length) {
        anyPolicy = true;
        allowed = new Set([...allowed].filter(t => types.includes(t)));
      }
    });
    if (!anyPolicy) return ruleLevel;
    const result = CORE_TYPES.filter(t => allowed.has(t));
    return result.length ? result : ruleLevel;
  };

  // Self-ship allowed if the rule allows it OR every selected reason allows it
  const isSelfShipAllowed = () => {
    if (selfShipAllowed) return true;
    const items = Object.values(selectedItems);
    if (!items.length || serverReasons.length === 0) return false;
    return items.every(item => (findReasonPolicy(item.reason)?.allowed_return_types || []).includes('self_ship'));
  };

  // Upload a photo for an item (reasons with photo_required)
  const handleItemPhotoUpload = async (sku, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }
    setUploadingPhotoSku(sku);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE_URL}/v1/returns/portal/upload-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSelectedItems(prev => ({
        ...prev,
        [sku]: { ...prev[sku], photos: [...(prev[sku]?.photos || []), res.data.url] }
      }));
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhotoSku(null);
    }
  };

  const removeItemPhoto = (sku, url) => {
    setSelectedItems(prev => ({
      ...prev,
      [sku]: { ...prev[sku], photos: (prev[sku]?.photos || []).filter(p => p !== url) }
    }));
  };
  
  // Update item notes
  const updateItemNotes = (sku, notes) => {
    setSelectedItems(prev => ({
      ...prev,
      [sku]: { ...prev[sku], reason_notes: notes }
    }));
  };
  
  // Calculate refund estimate
  const calculateRefund = () => {
    let total = 0;
    Object.values(selectedItems).forEach(item => {
      const orderItem = orderData?.items?.find(i => i.sku === item.sku);
      if (orderItem) {
        // effective_price = price actually paid after order-level discount
        const unit = parseFloat(orderItem.effective_price ?? orderItem.price) || 0;
        total += item.quantity_returning * unit;
      }
    });
    return total;
  };

  // Mirrors backend calculate_restocking_fee — primary (first) item's
  // reason policy applied on the return subtotal.
  const calculateRestockingFee = () => {
    const items = Object.values(selectedItems);
    const primary = items.find(i => i.reason);
    const policy = findReasonPolicy(primary?.reason);
    if (!policy?.restocking_fee_enabled) return 0;
    const subtotal = calculateRefund();
    const tiers = policy.restocking_fee_tiers || [];
    for (const tier of tiers) {
      const min = Number(tier.min_order_total || 0);
      const max = Number(tier.max_order_total || 0);
      if (subtotal >= min && (max <= 0 || subtotal <= max)) {
        return tier.fee_type === 'percentage'
          ? subtotal * (Number(tier.fee_value || 0) / 100)
          : Number(tier.fee_value || 0);
      }
    }
    return policy.restocking_fee_type === 'percentage'
      ? subtotal * (Number(policy.restocking_fee_value || 0) / 100)
      : Number(policy.restocking_fee_value || 0);
  };

  const calculateNetRefund = () => Math.max(0, calculateRefund() - calculateRestockingFee());
  
  // Proceed to type selection
  const handleProceedToType = () => {
    const items = Object.values(selectedItems);
    if (items.length === 0) {
      toast.error('Please select at least one item to return');
      return;
    }
    
    const missingReason = items.find(i => !i.reason);
    if (missingReason) {
      toast.error('Please select a return reason for all items');
      return;
    }

    // Photo requirement (per-reason policy)
    const missingPhoto = items.find(i => findReasonPolicy(i.reason)?.photo_required && !(i.photos || []).length);
    if (missingPhoto) {
      toast.error(`Please upload a photo for "${missingPhoto.product_name}" — required for the selected reason`);
      return;
    }

    // Snap return type + refund methods to the selected reasons' policies
    const allowed = getAllowedReturnTypes();
    if (allowed.length && !allowed.includes(returnType)) {
      setReturnType(allowed[0]);
    }
    const primaryPolicy = findReasonPolicy(items.find(i => i.reason)?.reason);
    if (primaryPolicy?.refund_method_options?.length) {
      setRefundMethodOptions(primaryPolicy.refund_method_options);
      const def = primaryPolicy.default_refund_method
        && primaryPolicy.refund_method_options.includes(primaryPolicy.default_refund_method)
        ? primaryPolicy.default_refund_method
        : primaryPolicy.refund_method_options[0];
      setRefundMethod(def);
    }
    if (!isSelfShipAllowed() && pickupPreference === 'self_ship') {
      setPickupPreference('courier');
    }
    
    setStep('select_type');
  };
  
  // Proceed to confirmation
  const handleProceedToConfirm = () => {
    setStep('confirm');
  };
  
  // Submit return request
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        order_identifier: orderNumber,
        email: email,
        return_type: returnType,
        items: Object.values(selectedItems),
        customer_notes: customerNotes,
        pickup_preference: pickupPreference,
        refund_method: refundMethod
      };
      
      const response = await axios.post(`${API_BASE_URL}/v1/returns/portal/request`, payload);
      
      setReturnResult(response.data);
      setStep('success');
      toast.success('Return request submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit return request');
    } finally {
      setLoading(false);
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Phase 3 — derive per-request theme / colors from the tenant's branding.
  const isLight = branding?.theme === 'light';
  const themeBg = isLight
    ? 'bg-gradient-to-br from-slate-50 via-white to-slate-100'
    : 'bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800';
  const themeText = isLight ? 'text-slate-900' : 'text-white';
  const themeMuted = isLight ? 'text-slate-500' : 'text-zinc-500';
  const primary = branding?.primary_color || '#8b5cf6';
  const cssVars = branding
    ? {
        '--brand-primary': primary,
        '--brand-accent': branding.accent_color || primary,
      }
    : {};

  return (
    <div className={`min-h-screen ${themeBg} ${themeText}`} style={cssVars}>
      {/* Phase 3 — tenant announcement bar */}
      {branding?.announcement_enabled && (branding.announcement_text || '').trim() && (
        <div
          className="w-full text-center text-xs sm:text-sm font-medium py-2 px-3"
          style={{
            backgroundColor: branding.announcement_bg_color,
            color: branding.announcement_text_color,
          }}
          data-testid="portal-announcement"
        >
          {branding.announcement_text}
        </div>
      )}

      {/* Header */}
      <div className={`${isLight ? 'bg-white/70 border-slate-200' : 'bg-black/30 border-white/5'} border-b`}>
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="brand logo" className="h-10 max-w-[160px] object-contain" data-testid="portal-logo" />
            ) : (
              <div className="p-2 bg-purple-500/20 rounded-xl">
                <RotateCcw className="w-6 h-6 text-purple-400" />
              </div>
            )}
            <div>
              <h1 className={`text-xl font-semibold ${themeText}`}>{branding?.store_name ? `${branding.store_name} — Returns` : 'Return Portal'}</h1>
              <p className={`text-sm ${themeMuted}`}>Request a return or exchange</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-8">
          {['lookup', 'select_items', 'select_type', 'confirm', 'success'].map((s, index) => {
            const stepLabels = ['Find Order', 'Select Items', 'Return Type', 'Confirm', 'Complete'];
            const isActive = step === s;
            const isPast = ['lookup', 'select_items', 'select_type', 'confirm', 'success'].indexOf(step) > index;
            
            return (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
                    ${isActive ? 'bg-purple-500 text-white' : isPast ? 'bg-green-500 text-white' : 'bg-zinc-800 text-zinc-500'}
                  `}>
                    {isPast ? <CheckCircle className="w-4 h-4" /> : index + 1}
                  </div>
                  <span className={`text-xs mt-1 ${isActive ? 'text-purple-400' : isPast ? 'text-green-400' : 'text-zinc-600'}`}>
                    {stepLabels[index]}
                  </span>
                </div>
                {index < 4 && (
                  <div className={`flex-1 h-0.5 mx-2 ${isPast ? 'bg-green-500' : 'bg-zinc-800'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        
        {/* Step Content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Order Lookup */}
          {step === 'lookup' && (
            <motion.div
              key="lookup"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white/[0.02] border border-white/5 rounded-2xl p-6"
            >
              <h2 className="text-lg font-medium text-white mb-1">Find Your Order</h2>
              <p className="text-sm text-zinc-500 mb-6">Enter your order number and email to start the return process</p>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-white">Order Number</Label>
                  <Input
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    placeholder="e.g., #12345 or WT26040001"
                    className="bg-white/5 border-white/10 text-white mt-1"
                    data-testid="order-number-input"
                  />
                </div>
                <div>
                  <Label className="text-white">Email Address</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="bg-white/5 border-white/10 text-white mt-1"
                    data-testid="email-input"
                  />
                </div>
                
                <Button
                  onClick={handleLookup}
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 mt-4"
                  data-testid="lookup-order-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Find Order
                </Button>
              </div>

              {/* Order found — always show details, eligible or not */}
              {lookupResult && orderData && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-6 rounded-2xl border overflow-hidden ${
                    lookupResult.eligible ? 'border-green-500/30' : 'border-red-500/30'
                  }`}
                  data-testid="lookup-order-details"
                >
                  {/* Eligibility banner */}
                  <div
                    className={`px-4 py-3 flex items-start gap-2 ${
                      lookupResult.eligible ? 'bg-green-500/10' : 'bg-red-500/10'
                    }`}
                    data-testid={lookupResult.eligible ? 'eligible-banner' : 'ineligible-banner'}
                  >
                    {lookupResult.eligible ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-green-300">Order found & eligible for return</p>
                          <p className="text-xs text-green-400/70">Review your order below and proceed</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-red-300">This order is not eligible for return</p>
                          <p className="text-xs text-red-400/80 mt-0.5" data-testid="ineligible-reason">
                            {lookupResult.reason || 'Order not eligible for return'}
                          </p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Order details */}
                  <div className="p-4 bg-white/[0.02]">
                    <div className="flex items-center justify-between pb-3 border-b border-white/5">
                      <div>
                        <p className="text-xs text-zinc-500">Order</p>
                        <p className="text-white font-medium" data-testid="found-order-number">{orderData.order_number}</p>
                        {orderData.customer_name && (
                          <p className="text-xs text-zinc-500">{orderData.customer_name}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-zinc-500">Order Date</p>
                        <p className="text-white text-sm">{formatDate(orderData.created_at)}</p>
                      </div>
                    </div>

                    <div className="py-3 space-y-2" data-testid="found-order-items">
                      {(orderData.items || []).map((item, idx) => (
                        <div key={item.sku || idx} className="flex items-center justify-between">
                          <div className="min-w-0 pr-3">
                            <p className="text-sm text-white truncate">{item.name || item.title}</p>
                            <p className="text-xs text-zinc-500 font-mono">
                              {item.sku ? `SKU: ${item.sku} · ` : ''}Qty: {item.quantity}
                            </p>
                          </div>
                          <p className="text-sm text-white shrink-0">{formatCurrency(item.price)}</p>
                        </div>
                      ))}
                    </div>

                    <div className="pt-3 border-t border-white/5 flex items-center justify-between">
                      <span className="text-sm text-zinc-400">Order Amount</span>
                      <span className="text-lg font-semibold text-white" data-testid="found-order-amount">
                        {formatCurrency(orderData.total_amount)}
                      </span>
                    </div>

                    {lookupResult.eligible && (
                      <Button
                        onClick={() => setStep('select_items')}
                        className="w-full bg-green-600 hover:bg-green-700 mt-4"
                        style={branding ? { backgroundColor: 'var(--brand-primary)' } : undefined}
                        data-testid="proceed-for-return-btn"
                      >
                        Proceed for Return
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
          
          {/* Step 2: Select Items */}
          {step === 'select_items' && orderData && (
            <motion.div
              key="select_items"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {/* Order Info */}
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-500">Order</p>
                    <p className="text-white font-medium">{orderData.order_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-zinc-500">Order Date</p>
                    <p className="text-white">{formatDate(orderData.created_at)}</p>
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Return window: {returnWindowDays} days from delivery
                </p>
              </div>

              {/* Store Credit bonus nudge */}
              {storeCreditBonusPercent > 0 && (
                <div className="flex items-center gap-3 p-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/10" data-testid="store-credit-bonus-banner">
                  <div className="p-2 bg-emerald-500/15 rounded-lg shrink-0">
                    <Wallet className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-emerald-300 font-medium">Choose Store Credit & get {storeCreditBonusPercent}% extra!</p>
                    <p className="text-xs text-emerald-200/70 mt-0.5">Pick "Store Credit" as your refund method and we'll add {storeCreditBonusPercent}% on top of your refund — no expiry.</p>
                  </div>
                </div>
              )}
              
              {/* Items Selection */}
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                <h3 className="text-white font-medium mb-4">Select items to return</h3>
                
                <div className="space-y-4">
                  {orderData.items?.map((item) => {
                    const isSelected = !!selectedItems[item.sku];
                    const selectedItem = selectedItems[item.sku];
                    const isExcluded = !!item.excluded;
                    
                    return (
                      <div
                        key={item.sku}
                        className={`p-4 rounded-xl border transition-all ${
                          isExcluded ? 'border-white/5 bg-white/[0.01] opacity-60'
                          : isSelected ? 'border-purple-500/50 bg-purple-500/5' : 'border-white/5 bg-white/[0.02]'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={isSelected}
                            disabled={isExcluded}
                            onCheckedChange={() => !isExcluded && toggleItem(item.sku)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-white font-medium">{item.name || item.title}</p>
                                <p className="text-xs text-zinc-500 font-mono">SKU: {item.sku}</p>
                                {item.variant_title && (
                                  <p className="text-xs text-zinc-500">{item.variant_title}</p>
                                )}
                                {isExcluded && (
                                  <span
                                    className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-500/15 text-red-300 border border-red-500/25"
                                    data-testid={`excluded-badge-${item.sku}`}
                                  >
                                    {item.excluded_reason || 'Not returnable'}
                                  </span>
                                )}
                                {isExcluded && item.excluded_message && (
                                  <p className="text-xs text-amber-300/90 mt-1.5" data-testid={`excluded-message-${item.sku}`}>
                                    {item.excluded_message}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-white font-medium">{formatCurrency(item.effective_price ?? item.price)}</p>
                                {item.effective_price != null && Number(item.effective_price) < Number(item.price) && (
                                  <p className="text-xs text-zinc-600 line-through">{formatCurrency(item.price)}</p>
                                )}
                                <p className="text-xs text-zinc-500">Qty: {item.quantity}</p>
                              </div>
                            </div>
                            
                            {/* Item Return Options (shown when selected) */}
                            {isSelected && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                className="mt-4 pt-4 border-t border-white/10 space-y-3"
                              >
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs text-zinc-400">Quantity to Return</Label>
                                    <Select
                                      value={selectedItem?.quantity_returning?.toString()}
                                      onValueChange={(v) => updateItemQuantity(item.sku, parseInt(v))}
                                    >
                                      <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent className="bg-zinc-900 border-white/10">
                                        {Array.from({ length: item.quantity }, (_, i) => (
                                          <SelectItem key={i + 1} value={(i + 1).toString()}>
                                            {i + 1}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-xs text-zinc-400">Reason *</Label>
                                    <Select
                                      value={selectedItem?.reason}
                                      onValueChange={(v) => updateItemReason(item.sku, v)}
                                    >
                                      <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1" data-testid={`reason-select-${item.sku}`}>
                                        <SelectValue placeholder="Select reason..." />
                                      </SelectTrigger>
                                      <SelectContent className="bg-zinc-900 border-white/10">
                                        {serverReasons.length > 0
                                          ? serverReasons.map(r => (
                                              <SelectItem key={r.id || r.name} value={r.name}>{r.name}</SelectItem>
                                            ))
                                          : returnReasons.map(r => (
                                              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                                            ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                {/* Reason help text (merchant-configured) */}
                                {findReasonPolicy(selectedItem?.reason)?.customer_help_text && (
                                  <p className="text-xs text-zinc-400 italic" data-testid={`reason-help-${item.sku}`}>
                                    {findReasonPolicy(selectedItem?.reason).customer_help_text}
                                  </p>
                                )}
                                {/* Photo upload — required by the selected reason */}
                                {findReasonPolicy(selectedItem?.reason)?.photo_required && (
                                  <div data-testid={`photo-upload-${item.sku}`}>
                                    <Label className="text-xs text-zinc-400">
                                      Photo <span className="text-red-400">*</span> (required for this reason)
                                    </Label>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                      {(selectedItem?.photos || []).map(url => (
                                        <div key={url} className="relative">
                                          <img src={url} alt="return" className="w-14 h-14 object-cover rounded-lg border border-white/10" />
                                          <button
                                            type="button"
                                            onClick={() => removeItemPhoto(item.sku, url)}
                                            className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] leading-4"
                                          >✕</button>
                                        </div>
                                      ))}
                                      <label className="w-14 h-14 rounded-lg border-2 border-dashed border-white/15 flex items-center justify-center cursor-pointer hover:border-white/30 text-zinc-500 text-xs">
                                        <input
                                          type="file"
                                          accept="image/*"
                                          className="hidden"
                                          onChange={(e) => handleItemPhotoUpload(item.sku, e.target.files?.[0])}
                                        />
                                        {uploadingPhotoSku === item.sku ? '...' : '+'}
                                      </label>
                                    </div>
                                  </div>
                                )}
                                <div>
                                  <Label className="text-xs text-zinc-400">Additional Details (optional)</Label>
                                  <Textarea
                                    value={selectedItem?.reason_notes || ''}
                                    onChange={(e) => updateItemNotes(item.sku, e.target.value)}
                                    className="bg-white/5 border-white/10 text-white mt-1"
                                    placeholder="Describe the issue..."
                                    rows={2}
                                  />
                                </div>
                              </motion.div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Summary & Continue */}
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                {!['replace_same', 'replace_different'].includes(returnType) && (
                  <>
                    {calculateRestockingFee() > 0 && (
                      <div className="mb-2 space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-zinc-500">Item Total</span>
                          <span className="text-zinc-300">{formatCurrency(calculateRefund())}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm" data-testid="restocking-fee-row">
                          <span className="text-zinc-500">Return Fee</span>
                          <span className="text-red-400">-{formatCurrency(calculateRestockingFee())}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-zinc-400">Estimated Refund</span>
                      <span className="text-xl font-semibold text-white" data-testid="estimated-refund">{formatCurrency(calculateNetRefund())}</span>
                    </div>
                  </>
                )}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep('lookup')}
                    className="border-white/10 text-white"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleProceedToType}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    style={branding ? { backgroundColor: 'var(--brand-primary)' } : undefined}
                    data-testid="proceed-to-type-btn"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Step 3: Select Return Type */}
          {step === 'select_type' && (
            <motion.div
              key="select_type"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                <h3 className="text-white font-medium mb-4">How would you like to proceed?</h3>
                
                <RadioGroup value={returnType} onValueChange={setReturnType} className="space-y-3">
                  {(() => {
                    const allowed = getAllowedReturnTypes();
                    return Object.entries(returnTypeLabels).map(([type, config]) => {
                      if (!allowed.includes(type)) return null;
                      const Icon = config.icon;
                    
                      return (
                        <label
                          key={type}
                          data-testid={`return-type-${type}`}
                          className={`
                            flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all
                            ${returnType === type 
                              ? 'border-purple-500/50 bg-purple-500/5' 
                              : 'border-white/5 bg-white/[0.02] hover:border-white/10'
                            }
                          `}
                        >
                          <RadioGroupItem value={type} className="border-white/20" />
                          <div className="p-2 bg-white/5 rounded-lg">
                            <Icon className="w-5 h-5 text-purple-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{config.label}</p>
                            <p className="text-sm text-zinc-500">{config.description}</p>
                          </div>
                        </label>
                      );
                    });
                  })()}
                </RadioGroup>
              </div>
              
              {/* Pickup Preference */}
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                <h3 className="text-white font-medium mb-4">Return Pickup Method</h3>
                
                <RadioGroup value={pickupPreference} onValueChange={setPickupPreference} className="space-y-3">
                  <label className={`
                    flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all
                    ${pickupPreference === 'courier' 
                      ? 'border-purple-500/50 bg-purple-500/5' 
                      : 'border-white/5 bg-white/[0.02] hover:border-white/10'
                    }
                  `}>
                    <RadioGroupItem value="courier" className="border-white/20" />
                    <div className="p-2 bg-white/5 rounded-lg">
                      <Truck className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Schedule Pickup</p>
                      <p className="text-sm text-zinc-500">We'll arrange a courier to pick up from your address</p>
                    </div>
                  </label>
                  
                  {isSelfShipAllowed() && (
                  <label className={`
                    flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all
                    ${pickupPreference === 'self_ship' 
                      ? 'border-purple-500/50 bg-purple-500/5' 
                      : 'border-white/5 bg-white/[0.02] hover:border-white/10'
                    }
                  `}>
                    <RadioGroupItem value="self_ship" className="border-white/20" />
                    <div className="p-2 bg-white/5 rounded-lg">
                      <Package className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Self Ship</p>
                      <p className="text-sm text-zinc-500">Ship the item yourself to our warehouse</p>
                    </div>
                  </label>
                  )}
                </RadioGroup>
              </div>
              
              {/* Phase 2 — Refund Method (only for refund-type returns with both options allowed) */}
              {['return_refund', 'refund_only'].includes(returnType) && refundMethodOptions.length > 1 && (
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                  <h3 className="text-white font-medium mb-4">How should we refund you?</h3>
                  <RadioGroup value={refundMethod} onValueChange={setRefundMethod} className="space-y-3">
                    {refundMethodOptions.includes('original') && (
                      <label className={`
                        flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all
                        ${refundMethod === 'original'
                          ? 'border-purple-500/50 bg-purple-500/5'
                          : 'border-white/5 bg-white/[0.02] hover:border-white/10'
                        }
                      `} data-testid="refund-method-original">
                        <RadioGroupItem value="original" className="border-white/20" />
                        <div className="p-2 bg-white/5 rounded-lg">
                          <CreditCard className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">Original Payment</p>
                          <p className="text-sm text-zinc-500">Refund in 4–7 business days</p>
                        </div>
                      </label>
                    )}
                    {refundMethodOptions.includes('store_credit') && (
                      <label className={`
                        flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all
                        ${refundMethod === 'store_credit'
                          ? 'border-purple-500/50 bg-purple-500/5'
                          : 'border-white/5 bg-white/[0.02] hover:border-white/10'
                        }
                      `} data-testid="refund-method-store-credit">
                        <RadioGroupItem value="store_credit" className="border-white/20" />
                        <div className="p-2 bg-white/5 rounded-lg">
                          <Wallet className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">Store Credit</p>
                            <Badge className="bg-emerald-500/20 text-emerald-300 text-[10px]">
                              Fast Refund
                            </Badge>
                            {storeCreditBonusPercent > 0 && (
                              <Badge className="bg-emerald-500/20 text-emerald-300 text-[10px]">
                                +{storeCreditBonusPercent}% extra
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-emerald-300/80">Fast refund • No expiry</p>
                          {storeCreditBonusPercent > 0 && (
                            <p className="text-sm text-zinc-500">
                              Get {formatCurrency(calculateNetRefund() * (1 + storeCreditBonusPercent / 100))} as store credit — spend it on your next order.
                            </p>
                          )}
                        </div>
                      </label>
                    )}
                  </RadioGroup>
                </div>
              )}

              {/* Notes */}
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                <Label className="text-white">Additional Notes (optional)</Label>
                <Textarea
                  value={customerNotes}
                  onChange={(e) => setCustomerNotes(e.target.value)}
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="Any additional information..."
                  rows={3}
                />
              </div>
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep('select_items')}
                  className="border-white/10 text-white"
                >
                  Back
                </Button>
                <Button
                  onClick={handleProceedToConfirm}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  style={branding ? { backgroundColor: 'var(--brand-primary)' } : undefined}
                  data-testid="proceed-to-confirm-btn"
                >
                  Review & Confirm
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </motion.div>
          )}
          
          {/* Step 4: Confirmation */}
          {step === 'confirm' && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-4">
                <h3 className="text-white font-medium mb-4">Review Your Return Request</h3>
                
                {/* Order Info */}
                <div className="flex items-center justify-between pb-4 border-b border-white/5">
                  <div>
                    <p className="text-sm text-zinc-500">Order</p>
                    <p className="text-white font-medium">{orderData?.order_number}</p>
                  </div>
                  <Badge className="bg-purple-500/20 text-purple-300">
                    {returnTypeLabels[returnType]?.label}
                  </Badge>
                </div>
                
                {/* Items */}
                <div className="py-4 border-b border-white/5">
                  <p className="text-sm text-zinc-500 mb-3">Items to Return</p>
                  {Object.values(selectedItems).map(item => (
                    <div key={item.sku} className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-white">{item.product_name}</p>
                        <p className="text-xs text-zinc-500">Qty: {item.quantity_returning} × {findReasonPolicy(item.reason)?.name || returnReasons.find(r => r.value === item.reason)?.label || item.reason}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pickup Method */}
                <div className="py-4 border-b border-white/5">
                  <p className="text-sm text-zinc-500 mb-1">Pickup Method</p>
                  <p className="text-white">{pickupPreference === 'courier' ? 'Scheduled Pickup' : 'Self Ship'}</p>
                </div>
                
                {/* Refund Amount — hidden for replacements (no money changes hands) */}
                {['replace_same', 'replace_different'].includes(returnType) ? (
                  <div className="pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400">Resolution</span>
                      <span className="text-lg font-semibold text-blue-400" data-testid="confirm-replacement-note">Replacement — no refund</span>
                    </div>
                    <p className="text-xs text-zinc-600 mt-1">
                      A replacement will be shipped after QC inspection of your returned item
                    </p>
                  </div>
                ) : (
                <div className="pt-4">
                  {calculateRestockingFee() > 0 && (
                    <div className="mb-2 space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-500">Item Total</span>
                        <span className="text-zinc-300">{formatCurrency(calculateRefund())}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm" data-testid="confirm-restocking-fee-row">
                        <span className="text-zinc-500">Return Fee</span>
                        <span className="text-red-400">-{formatCurrency(calculateRestockingFee())}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">Estimated Refund</span>
                    <span className="text-xl font-semibold text-green-400" data-testid="confirm-estimated-refund">{formatCurrency(calculateNetRefund())}</span>
                  </div>
                  <p className="text-xs text-zinc-600 mt-1">
                    Final refund amount may vary after QC inspection
                  </p>
                </div>
                )}
              </div>
              
              {/* Review disclaimer */}
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20" data-testid="review-disclaimer">
                <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-200/90">
                  Your return request will be reviewed by our team, who will decide whether it is approved.
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep('select_type')}
                  className="border-white/10 text-white"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="submit-return-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Submit Return Request
                </Button>
              </div>
            </motion.div>
          )}
          
          {/* Step 5: Success */}
          {step === 'success' && returnResult && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/[0.02] border border-white/5 rounded-2xl p-8 text-center"
            >
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              
              <h2 className="text-xl font-semibold text-white mb-2">Return Request Submitted!</h2>
              <p className="text-zinc-400 mb-6" data-testid="success-review-note">
                Your return request will be reviewed by our team, who will decide whether it is approved. We'll notify you once a decision is made.
              </p>
              
              <div className="bg-white/[0.02] rounded-xl p-4 mb-6">
                <p className="text-sm text-zinc-500 mb-1">Return Number</p>
                <p className="text-2xl font-mono font-bold text-purple-400">{returnResult.return_number}</p>
                <p className="text-xs text-zinc-600 mt-2">Save this number for tracking your return</p>
              </div>
              
              <div className="text-sm text-zinc-500 space-y-2">
                <p>• You will receive an email confirmation shortly</p>
                {pickupPreference === 'courier' && (
                  <p>• We will contact you to schedule the pickup</p>
                )}
                {pickupPreference === 'self_ship' && (
                  <p>• Ship items to our warehouse address (sent via email)</p>
                )}
                <p>• Refund will be processed within 5-7 business days after QC</p>
              </div>
              
              <Button
                onClick={() => {
                  setStep('lookup');
                  setOrderNumber('');
                  setEmail('');
                  setOrderData(null);
                  setSelectedItems({});
                  setReturnResult(null);
                  setLookupResult(null);
                }}
                variant="outline"
                className="mt-6 border-white/10 text-white"
              >
                Start New Return
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Footer — shows tenant-specific support contacts + footer note when available */}
      <div className={`max-w-3xl mx-auto px-4 py-8 text-center text-xs ${themeMuted}`} data-testid="portal-footer">
        {branding && (branding.support_email || branding.support_phone || branding.footer_note) ? (
          <div className="space-y-1">
            {branding.footer_note && <p className="italic">{branding.footer_note}</p>}
            {(branding.support_email || branding.support_phone) && (
              <p>
                Need help? {branding.support_email && (
                  <a href={`mailto:${branding.support_email}`} className="underline hover:opacity-80">
                    {branding.support_email}
                  </a>
                )}
                {branding.support_email && branding.support_phone && ' · '}
                {branding.support_phone && (
                  <a href={`tel:${branding.support_phone}`} className="underline hover:opacity-80">
                    {branding.support_phone}
                  </a>
                )}
              </p>
            )}
          </div>
        ) : (
          <p>Need help? Contact our support team</p>
        )}
      </div>
    </div>
  );
};

export default CustomerReturnPortal;
